import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TextFieldElement } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { NestedGeographicBoundarySelect } from './NestedGeographicBoundarySelect';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from './PhoneInput';

const contactSchema = z.object({
  email: z.object({
    id: z.number().optional(),
    address: z.string().email(),
  }),
  phone: z
    .object({
      id: z.number().optional(),
      countryCode: z.string(),
      number: z.string(),
    })
    .refine(
      (data) => isValidPhoneNumber('+' + data.countryCode + data.number),
      {
        message: 'contactForm.phoneInvalid',
      },
    ),
  address: z.object({
    id: z.number().optional(),
    geographicBoundaryId: z.number().optional().nullable(),
    street1: z.string(),
    doorNumber: z.string().min(1),
  }),
});

export type ContactFormValues = z.infer<typeof contactSchema>;

export function ContactForm(props: {
  initialValues?: z.infer<typeof contactSchema>;
  onSubmit: (data: z.infer<typeof contactSchema>) => void;
}) {
  const [initialValueApplied, setInitialValueApplied] = useState(false);
  const { t } = useTranslation();
  const ref = useRef<HTMLFormElement>(null);

  // added to extract key
  t('contactForm.phoneInvalid');
  const form = useForm<z.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
    defaultValues: props.initialValues ?? {
      email: { address: '' },
      phone: {
        countryCode: '',
        number: '',
      },
      address: {
        geographicBoundaryId: undefined,
        street1: '',
        doorNumber: undefined,
      },
    },
  });

  useEffect(() => {
    if (props.initialValues?.email.id)
      form.setValue('email.id', props.initialValues.email.id);
    if (props.initialValues?.phone.id)
      form.setValue('phone.id', props.initialValues.phone.id);
    if (props.initialValues?.address.id)
      form.setValue('address.id', props.initialValues.address.id);
  }, [form, props.initialValues]);

  const handleSubmit = form.handleSubmit((data) => {
    props.onSubmit(data);
  });

  // reset persisted phone number when invalid
  useEffect(() => {
    if (form.formState.errors.phone) {
      props.onSubmit({
        ...form.getValues(),
        phone: {
          countryCode: '',
          number: '',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState.errors.phone]);

  const values = form.watch([
    'address.doorNumber',
    'address.geographicBoundaryId',
    'address.street1',
    'email.address',
    'phone.countryCode',
    'phone.number',
  ]);
  useEffect(() => {
    // skip first render
    if (!initialValueApplied) return setInitialValueApplied(true);
    console.log('values changed submitting', values);
    // skip for not valid phone number
    if (!isValidPhoneNumber('+' + values[4] + values[5])) {
      return;
    }
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, values);

  return (
    <form onSubmit={handleSubmit} ref={ref}>
      <Stack spacing={2} direction="column">
        <TextFieldElement
          control={form.control}
          name="email.address"
          label="Email"
        />
        <PhoneInput
          label={t('contactForm.phone')}
          value={
            form.getValues('phone.countryCode')
              ? '+' +
                form.getValues('phone.countryCode') +
                form.getValues('phone.number')
              : ''
          }
          onChange={(data) => {
            const parsed = parsePhoneNumber(data || '');
            if (!parsed) {
              return;
            }
            form.setValue('phone.countryCode', parsed.countryCallingCode);
            form.setValue('phone.number', parsed.nationalNumber);
          }}
          helperText={
            form.formState.errors.phone?.message &&
            t(form.formState.errors.phone?.message)
          }
          error={!!form.formState.errors.phone}
        />
        <NestedGeographicBoundarySelect
          value={form.getValues('address.geographicBoundaryId') || undefined}
          onChange={(value) =>
            form.setValue('address.geographicBoundaryId', value)
          }
          formDirty={form.formState.isDirty}
        />
        <TextFieldElement
          control={form.control}
          name="address.doorNumber"
          label={t('contactForm.doorNumber')}
        />
        <TextFieldElement
          control={form.control}
          name="address.street1"
          label={t('contactForm.street1')}
        />
      </Stack>
      <Stack direction="row" justifyContent="end" sx={{ mt: 2 }} spacing={1}>
        <Button
          variant="text"
          type="reset"
          color="inherit"
          onClick={() =>
            form.reset({
              email: { address: '' },
              phone: {
                countryCode: '',
                number: '',
              },
              address: {
                geographicBoundaryId: null,
                street1: '',
                doorNumber: '',
              },
            })
          }
        >
          {t('contactForm.reset')}
        </Button>
        <Button
          variant="contained"
          type="button"
          onClick={async () => {
            await handleSubmit();
            if (form.formState.isValid) {
              window.scrollTo({
                top:
                  (ref.current?.offsetTop || 0) +
                  (ref.current?.offsetHeight || 0),
                behavior: 'smooth',
              });
            }
          }}
          sx={{
            display: { md: 'none' },
          }}
        >
          {t('contactForm.continue')}
        </Button>
      </Stack>
    </form>
  );
}
